import classNames from 'classnames';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { TypeAnimation } from 'react-type-animation';

import BgShapeImage from '@/assets/CNC-rfq/bg-shape.png';
import Button from '@/components/Button';
import type { HeadlinePart } from '@/components/Landing/AnimatedHeadline';
import { AnimatedHeadline } from '@/components/Landing/AnimatedHeadline';
import ContactFormSheet from '@/components/Landing/ContactFormSheet';
import Text from '@/components/Text';

import CncRfqUpload from './CncRfqUpload';

const processWords = [
  'laser cutting',
  'sheet-metal bending',
  'CNC milling',
  'CNC turning',
  'welding',
  'tube cutting',
];

const headlineParts: HeadlinePart[] = [
  { id: 'send', text: 'Send' },
  { id: 'the-1', text: 'the' },
  { id: 'quote', text: 'quote' },
  { id: 'before', text: 'before', br: true },
  { id: 'your', text: 'your' },
  { id: 'competitor', text: 'competitor' },
  { id: 'even', text: 'even' },
  { id: 'opens', text: 'opens' },
  { id: 'the-2', text: 'the' },
  { id: 'rfq', text: 'RFQ' },
];

const CncRfqHero = () => {
  return (
    <div className="relative flex w-full flex-col items-center gap-6 overflow-hidden px-4 pb-16 pt-32 md:px-10 md:pt-40">
      <Text className="text-neutral-400" as="p">
        Quoting for{' '}
        <span className="text-neutral-0">
          <TypeAnimation
            speed={{ type: 'keyStrokeDelayInMs', value: 55 }}
            preRenderFirstString
            sequence={processWords.flatMap((word) => [word, 1250])}
            repeat={Infinity}
          />
        </span>
      </Text>

      <AnimatedHeadline
        parts={headlineParts}
        className="max-w-4xl !text-[32px] md:!text-[42px] lg:max-w-[940px] lg:!text-[52px]"
      />

      <Text className="max-w-2xl text-center text-sm text-jb-neutral-600 md:text-lg lg:max-w-[600px] lg:!text-[20px]">
        Upload a CAD file and MindPal turns complex manufacturing data into an
        instant, itemized quote: material, cycle time, margin and lead time, in
        about 30 seconds.
      </Text>

      <div className="flex flex-row flex-wrap items-center justify-center gap-3">
        <ContactFormSheet sheetTriggerProps={{ asChild: true }}>
          <Button
            variant="cncSecondary"
            className="!gap-1 !text-[19px] text-neutral-0 [&_svg]:!h-6 [&_svg]:!w-6 [&_svg]:!rotate-[-135deg]"
            suffixIcon="material-symbols:arrow-downward"
          >
            Let&apos;s talk
          </Button>
        </ContactFormSheet>
      </div>

      <Text className="text-center text-body-small text-jb-neutral-600">
        No signup · No credit card
      </Text>

      <div className="relative mt-2 w-full max-w-4xl">
        <Image
          src={BgShapeImage}
          alt="gradient background shape"
          className="pointer-events-none absolute left-1/2 top-1/3 -z-50 h-auto w-[190%] max-w-none -translate-x-1/2 -translate-y-1/2 sm:w-[165%] md:w-[145%] lg:w-[105%]"
        />
        <motion.div
          id="cnc-rfq-upload"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }}
          className={classNames('relative z-10 w-full')}
        >
          <CncRfqUpload />
        </motion.div>
      </div>
    </div>
  );
};

export default CncRfqHero;
